// Battery and Wi-Fi in the kiosk header. The kiosk hides Android's status bar, so without this
// nobody could see a scanner's charge until it died. Renders nothing outside the shell or on a
// shell too old to report status. Status is never colour alone: the percentage is always printed,
// and signal is the number of lit bars.

import React, { useEffect, useState } from 'react';
import { getShell } from './shell';

interface Status {
  battery: number | null;
  charging: boolean;
  wifi: number | null;
}

const POLL_MS = 20_000;
const LOW = 20;

function read(): Status | null {
  try {
    const raw = getShell()?.status?.();
    if (!raw) return null;
    const s = JSON.parse(raw);
    return { battery: s.battery ?? null, charging: !!s.charging, wifi: s.wifi ?? null };
  } catch {
    return null;
  }
}

const Battery: React.FC<{ level: number; charging: boolean }> = ({ level, charging }) => {
  const low = level < LOW && !charging;
  const fillWidth = Math.max(1, Math.round((level / 100) * 14));
  return (
    <span className={`inline-flex items-center gap-1 text-xs tabular-nums ${low ? 'text-danger' : 'text-on-surface-variant'}`}>
      <svg width="22" height="12" viewBox="0 0 22 12" fill="none" aria-hidden="true">
        {/* The case: an outer box minus an inner one, so it is a filled shape, not a stroke. */}
        <path fillRule="evenodd" clipRule="evenodd" fill="currentColor"
          d="M2 0h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2Zm0 1.5a.5.5 0 0 0-.5.5v8a.5.5 0 0 0 .5.5h16a.5.5 0 0 0 .5-.5V2a.5.5 0 0 0-.5-.5H2Z" />
        <path fill="currentColor" d="M20.5 4h.5a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-.5V4Z" />
        <rect x="3" y="3" width={fillWidth} height="6" rx="0.5" fill="currentColor" />
      </svg>
      {/* Its own glyph beside the case: drawn inside it, at this size, it vanished. */}
      {charging && (
        <svg width="8" height="12" viewBox="0 0 8 12" role="img" aria-label="Charging" className="fill-primary">
          <title>Charging</title>
          <path d="M5.2 0 0 6.9h3.3L2.4 12 8 4.9H4.6L5.2 0Z" />
        </svg>
      )}
      <span>{level}%</span>
    </span>
  );
};

const Wifi: React.FC<{ bars: number | null }> = ({ bars }) => {
  const lit = bars ?? 0;
  const label = bars == null ? 'Not on Wi-Fi' : `Wi-Fi signal ${lit} of 4`;
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" role="img" aria-label={label}>
      <title>{label}</title>
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={i * 4} y={9 - i * 3} width="3" height={3 + i * 3} rx="0.5"
          className={i < lit ? 'fill-on-surface-variant' : 'fill-surface-container-highest'} />
      ))}
    </svg>
  );
};

const ShellStatus: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(read);

  useEffect(() => {
    const t = setInterval(() => setStatus(read()), POLL_MS);
    return () => clearInterval(t);
  }, []);

  if (!status) return null;
  return (
    <span className="inline-flex items-center gap-2.5">
      <Wifi bars={status.wifi} />
      {status.battery != null && <Battery level={status.battery} charging={status.charging} />}
    </span>
  );
};

export default ShellStatus;
